/**
 * Build the DataGrid column model for a saved Initial assessment's Summary
 * Statistics table.
 *
 * A saved Initial assessment stores its rows nested as
 *   { statistic, values: { <parameter>: value } }
 * (see `mergeSummaryStatistics` in chart.js). The value columns must therefore
 * be derived from the assessment's OWN data and read the nested `values[param]`
 * via a valueGetter. The previous approach built columns from the currently
 * viewed chart and read a flat `row[param]`, which:
 *   - dropped every value column whenever the chart showed a different
 *     parameter than the saved assessment, and
 *   - never matched the nested rows even when the column survived,
 * leaving the whole Initial assessment form blank.
 *
 * Falls back to the legacy current-chart columns for any assessment whose rows
 * are still in the old flat shape (no `values` object).
 *
 * @param {Array} summaryStatistics saved assessment rows
 * @param {Array} fallbackColumns   current-chart-derived columns (legacy path)
 * @param {Object} modalAssessment  the saved assessment (for the legacy filter)
 * @returns {Array} DataGrid column definitions
 */
export function buildInitialAssessmentColumns(
  summaryStatistics = [],
  fallbackColumns = [],
  modalAssessment = {}
) {
  const valueKeys = [];
  (summaryStatistics || []).forEach((row) => {
    if (row && row.values && typeof row.values === 'object') {
      Object.keys(row.values).forEach((key) => {
        if (!valueKeys.includes(key)) valueKeys.push(key);
      });
    }
  });

  if (valueKeys.length) {
    return [
      { field: 'statistic', headerName: 'Statistic', flex: 1, sortable: false, width: 200 },
      ...valueKeys.map((key) => ({
        // The `field` is just a stable column id (the value is read from the
        // nested `values` object via valueGetter, not `row[field]`), so the raw
        // parameter key is fine and stays unique across columns.
        field: key,
        headerName: key,
        flex: 1,
        sortable: false,
        valueGetter: (params) => params?.row?.values?.[key]
      }))
    ];
  }

  // Legacy fallback: flat rows keyed by the current chart's columns.
  if (modalAssessment.fullKey && modalAssessment.fullKey !== modalAssessment.shortKey) {
    const headerKey = modalAssessment?.fullKey?.split('|')[4];
    return fallbackColumns.filter(
      (item) =>
        item.headerName === headerKey ||
        item.headerName === 'Statistic' ||
        (item.headerName.includes(headerKey) && modalAssessment.archive)
    );
  }
  return fallbackColumns;
}

export default buildInitialAssessmentColumns;
